import React, {ReactElement, useContext, useEffect, useState} from "react";
import {Field, Input, Label, Range, InputGroup, Select, Hint} from "@zendeskgarden/react-forms";
import {
    EXECUTIVE_SUMMARY_DEFAULT_PRIMARY_COLOR,
    EXECUTIVE_SUMMARY_DEFAULT_SECONDARY_COLOR
} from "../../property-executive-summary/constants";
import {FileUploadField} from "../../../components/forms/FileUploadField";
import {Button} from "@zendeskgarden/react-buttons";
import {
    GetBrandingInformationQuery,
    useGetBrandingInformationLazyQuery,
    useGetBrandingUploadUrlsMutation,
    useSetBrandingInformationMutation
} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";
import ExecutiveSummaryBanner from "../../property-executive-summary/components/ExecutiveSummaryBanner";
import * as execSumCSS from "../../../pages/property-executive-summary/styles/css.module.scss";
import * as css from "../styles/executiveSummaryReportManagement.module.scss";
import { ColorpickerDialog, IColor} from '@zendeskgarden/react-colorpickers';
import { AuthContext } from "../../../contexts/AuthContext";
import ExecutiveSummaryPDFCoverPreview from "./ExecutiveSummaryPDFCoverPreview";


interface ExecutiveSummaryBrandingFormProps {
    brandingInformation: GetBrandingInformationQuery | undefined;
    brandingInformationLoading: boolean;
}

export function ExecutiveSummaryBrandingForm({brandingInformation, brandingInformationLoading}: ExecutiveSummaryBrandingFormProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [clientDisplayName, setClientDisplayName] = useState<string>("");
    const [primaryColor, setPrimaryColor] = useState<string>(EXECUTIVE_SUMMARY_DEFAULT_PRIMARY_COLOR);
    const [secondaryColor, setSecondaryColor] = useState<string>(EXECUTIVE_SUMMARY_DEFAULT_SECONDARY_COLOR);
    const [background, setBackground] = useState<File | undefined>(undefined);
    const [logo, setLogo] = useState<File | undefined>(undefined);
    const [logoMini, setLogoMini] = useState<File | undefined>(undefined);
    const [bannerCollapsed, setBannerCollapsed] = useState<boolean>(false);
    const [bannerLogoSize, setBannerLogoSize] = useState<number>(100);
    const [pdfLogoSize, setPDFLogoSize] = useState<number>(50);
    const [bannerImagePosition, setBannerImagePosition] = useState<string>("center");
    const [pdfImagePosition, setPDFImagePosition] = useState<string>("center");
    const [footerLogoSize, setFooterLogoSize] = useState<number>(100);

    const [previewBackgroundImage, setPreviewBackgroundImage] = useState<string | null>(null);
    const [previewLogoImage, setPreviewLogoImage] = useState<string | null>(null);
    const [previewLogoMiniImage, setPreviewLogoMiniImage] = useState<string | null>(null);

    const [isValid, setIsValid] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const [getBrandingUploadUrls] = useGetBrandingUploadUrlsMutation();
    const [setBrandingInformation] = useSetBrandingInformationMutation();

    function reset() {
        setBackground(undefined);
        setLogo(undefined);
        setLogoMini(undefined);
    }

    function setBranding() {
        setSaving(true);

        getBrandingUploadUrls({
            variables: {
                backgroundImage: background?.name,
                logo: logo?.name,
                logoMini: logoMini?.name,
            }
        }).then(res => {
            if(!res.data || !res.data.getBrandingUploadUrlsAllowNull) {
                throw new Error("Failed to get branding upload urls");
            }

            const brandingUrls = res.data.getBrandingUploadUrlsAllowNull;
            const uploadPromises = [];

            if (background && brandingUrls.backgroundImage) {
                uploadPromises.push(fetch(brandingUrls.backgroundImage, {
                    method: 'PUT',
                    body: background,
                    headers: {
                        'Content-Type': background.type,
                    }
                }));
            }

            if (logo && brandingUrls.logo) {
                uploadPromises.push(fetch(brandingUrls.logo, {
                    method: 'PUT',
                    body: logo,
                    headers: {
                        'Content-Type': logo.type,
                    }
                }));
            }

            if (logoMini && brandingUrls.logoMini) {
                uploadPromises.push(fetch(brandingUrls.logoMini, {
                    method: 'PUT',
                    body: logoMini,
                    headers: {
                        'Content-Type': logoMini.type,
                    }
                }));
            }
            return Promise.all(uploadPromises);
        }).then(res => {
            if(res.count(r => !r.ok) > 0) {
                throw new Error("Failed to upload branding images");
            }

            return setBrandingInformation({
                variables: {
                    input: {
                        clientDisplayName,
                        backgroundImage: background?.name,
                        logo: logo?.name,
                        logoMini: logoMini?.name,
                        primaryColor,
                        secondaryColor,
                        bannerLogoSize,
                        pdfLogoSize,
                        bannerImagePosition,
                        pdfImagePosition,
                        footerLogoSize,
                    }
                }
            });
        }).then(res => {
            if(!res.data || !res.data.setBrandingInformation) {
                throw new Error("Failed to set branding information");
            }

            reset();
            toast.success("Branding information updated.");
        }).catch(err => {
            console.error(err);
            toast.error("Failed to upload branding information.");
        }).finally(() => {
            setSaving(false);
        });
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setBranding();
    }

    function isValidCheck(): boolean {
        if (brandingInformationLoading) {
            return false;
        }

        if (clientDisplayName.trim() == "" || !primaryColor || !secondaryColor) {
            return false;
        }

        return true;
    }

    useEffect(() => {
        if (background) {
            setPreviewBackgroundImage(background ? URL.createObjectURL(background) : null);
        }
    }, [background]);

    useEffect(() => {
        if (logo) {
            setPreviewLogoImage(logo ? URL.createObjectURL(logo) : null);
        }
    }, [logo]);

    useEffect(() => {
        if (logoMini) {
            setPreviewLogoMiniImage(logoMini ? URL.createObjectURL(logoMini) : null);
        }
    }, [logoMini]);

    useEffect(() => {
        setIsValid(isValidCheck());
    }, [clientDisplayName, primaryColor, secondaryColor]);

    useEffect(() => {
        if (brandingInformation?.getBrandingInformationAllowNulls) {
            const brandInfo = brandingInformation.getBrandingInformationAllowNulls;

            setClientDisplayName(brandInfo.clientDisplayName ?? "Client Name");
            setPrimaryColor(brandInfo.primaryColor);
            setSecondaryColor(brandInfo.secondaryColor);
            setBannerLogoSize(brandInfo.bannerLogoSize ?? 100);
            setBannerImagePosition(brandInfo.bannerImagePosition ?? "center");
            setFooterLogoSize(brandInfo.footerLogoSize ?? 100);
            setPDFImagePosition(brandInfo.pdfImagePosition ?? "center");

            if (brandInfo.backgroundImage) {
                setPreviewBackgroundImage(brandInfo.backgroundImage);
            }
            if (brandInfo.logo) {
                setPreviewLogoImage(brandInfo.logo);
            }
            if (brandInfo.logoMini) {
                setPreviewLogoMiniImage(brandInfo.logoMini);
            }
        } else if (user) {
            setClientDisplayName(user.clientName);
        }
    }, [brandingInformation?.getBrandingInformationAllowNulls, user]);

    // function pulled from zendesk colorpicker component sample https://zendeskgarden-v8.netlify.app/components/color-picker
    function toHex(selectedColor: IColor) {
        let colorHex = selectedColor.hex;

        if (selectedColor.alpha === 100) {
            return colorHex;
        }

        let alphaHex = Math.round((selectedColor.alpha / 100) * 255).toString(16);

        if (alphaHex.length === 1) {
            alphaHex = `0${alphaHex}`;
        }

        if (colorHex.length === 4) {
            if (alphaHex.charAt(0) === alphaHex.charAt(1)) {
                alphaHex = alphaHex.charAt(0);
            } else {
                colorHex = `#${colorHex.charAt(1)}${colorHex.charAt(1)}${colorHex.charAt(2)}${colorHex.charAt(
                    2
                )}${colorHex.charAt(3)}${colorHex.charAt(3)}`;
            }
        }

        return `${colorHex}${alphaHex}`;
    }

    return <div className={css.brandingWrapper}>
        <div style={{width: "100%", height: "6px", backgroundColor: secondaryColor}}></div>
        <ExecutiveSummaryBanner
            className={execSumCSS.onConfigPage}
            budgetYear={new Date().getFullYear()}
            clientName={clientDisplayName}
            propertyName={"Bowman Estates"}
            bannerImg={previewBackgroundImage}
            logoImg={previewLogoImage}
            colorPrimary={primaryColor}
            bannerImgPosition={bannerImagePosition}
            logoSize={bannerLogoSize}
            isCollapsed={bannerCollapsed}
        />

        <div className={css.formWrapper}>
            <form onSubmit={handleSubmit}>
                <div className={css.settingsTitle}>
                    <h4>Banner Settings</h4>
                    <Button onClick={() => setBannerCollapsed(!bannerCollapsed)}>{bannerCollapsed ? "Expand" : "Collapse"} Banner</Button>
                </div>
                <div className={css.settingsRow}>
                    <div style={{width: "350px"}}>
                        <Field style={{marginBottom: "1rem"}}>
                            <Label>Presented By</Label>
                            <Input type="text" value={clientDisplayName} onChange={e => setClientDisplayName(e.target.value)} />
                        </Field>

                        <div className={css.colorInputGroup}>
                            <Field>
                                <Label>Banner Color</Label>
                                <InputGroup style={{ zIndex: 1 }}>
                                    <Input
                                        style={{width: "90px"}}
                                        maxLength={9}
                                        value={primaryColor}
                                        onChange={e => {
                                            setPrimaryColor(e.target.value);
                                        }}
                                    />
                                    <ColorpickerDialog
                                        style={{zIndex: 2}}
                                        focusInset
                                        color={primaryColor}
                                        isOpaque
                                        onChange={selectedColor => {
                                            setPrimaryColor(toHex(selectedColor));
                                        }}
                                    />
                                </InputGroup>
                            </Field>
                            <Field>
                                <Label>Accent Color</Label>
                                <InputGroup style={{ zIndex: 1 }}>
                                    <Input
                                        style={{width: "90px"}}
                                        maxLength={9}
                                        value={secondaryColor}
                                        onChange={e => {
                                            setSecondaryColor(e.target.value);
                                        }}
                                    />
                                    <ColorpickerDialog
                                        style={{zIndex: 2}}
                                        focusInset
                                        color={secondaryColor}
                                        isOpaque
                                        onChange={selectedColor => {
                                            setSecondaryColor(toHex(selectedColor));
                                        }}
                                    />
                                </InputGroup>
                            </Field>
                        </div>
                    </div>

                    <div className={css.imageConfigGroup}>
                        <FileUploadField value={background} onChange={setBackground} label="Banner Image" message="Background for executive summary." />
                        <Field>
                            <Label>Banner Image Position</Label>
                            <Hint>Depending on image aspect ratio, you may not see a change</Hint>
                            <Select disabled={!previewBackgroundImage} value={bannerImagePosition} onChange={(evt) => setBannerImagePosition(evt.target.value)}>
                                <option value="top">Top</option>
                                <option value="center">Center</option>
                                <option value="bottom">Bottom</option>
                            </Select>
                        </Field>
                    </div>

                    <div className={css.imageConfigGroup}>
                        <FileUploadField value={logo} onChange={setLogo} label="Logo - White" message="Logo for executive summary header." />
                        <Field>
                            <Label style={{display: "block"}}>Banner Logo Size {bannerLogoSize}px</Label>
                            <Range disabled={!previewLogoImage} style={{width: "100%"}} value={bannerLogoSize} step={1} min={0} max={200} onChange={(evt) => setBannerLogoSize(+evt.target.value)} />
                        </Field>
                        {previewLogoImage &&
                            <div className={css.darkLogoWrapper}>
                                <img src={previewLogoImage} style={{display: "block", width: `${bannerLogoSize}px`, margin: "1rem auto"}} />
                            </div>
                        }
                    </div>

                    <div className={css.imageConfigGroup}>
                        <FileUploadField value={logoMini} onChange={setLogoMini} label="Logo - Color" message="Logo for executive summary footer." />
                        <Field>
                            <Label>Footer Logo Size {footerLogoSize}px</Label>
                            <Range disabled={!previewLogoMiniImage} style={{width: "100%"}} value={footerLogoSize} step={1} min={0} max={200} onChange={(evt) => setFooterLogoSize(+evt.target.value)} />
                        </Field>
                        {previewLogoMiniImage &&
                            <img src={previewLogoMiniImage} style={{display: "block", width: `${footerLogoSize}px`, margin: "1rem auto"}} />
                        }
                    </div>
                </div>

                <div className={css.settingsTitle}>
                    <h4>PDF Cover Settings</h4>
                </div>
                <div className={css.settingsRow}>
                    <ExecutiveSummaryPDFCoverPreview
                        clientDisplayName={clientDisplayName}
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        previewBackgroundImage={previewBackgroundImage}
                        previewLogoImage={previewLogoImage}
                        pdfImagePosition={pdfImagePosition}
                        pdfLogoSize={pdfLogoSize}
                        onPositionChange={setPDFImagePosition}
                        onSizeChange={setPDFLogoSize}
                    />
                </div>

                <div className={css.formFooter}>
                    <Button type="submit" isPrimary disabled={!isValid || saving}>Update Branding</Button>
                </div>
            </form>
        </div>

    </div>;
}
